import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { actions as topikActions } from "../../../../modules/topik";
import { commitHaptic } from "../haptics";
import AnalysisFrame from "./AnalysisFrame";
import AnalysisRecall from "./AnalysisRecall";

// The voluntary stop/continue gate (§6.2a a) - Q2's "skippable-with-friction" as one
// deliberate choice: stop completes the session, continue opens the elaboration + goal steps.
// The instruction reminds the player they "named the risk and the idea"; carry both onto the
// screen so the choice to go deeper is made with the actual answers in view (memory-load).
const TopikContinueView = ({ t, session, topikStatus, gameClosed, dispatch }) => {
  const choose = (shouldContinue) => {
    if (topikStatus.submitting || gameClosed) return;
    commitHaptic();
    dispatch(topikActions.analysis.continue.request({ continue: shouldContinue }));
  };

  return (
    <AnalysisFrame
      t={t}
      title={t("topik-continue-view.title")}
      instruction={t("topik-continue-view.instruction")}
      heroIcon={<AltRouteIcon />}
      canBack
      submitting={topikStatus.submitting}
      gameClosed={gameClosed}
      error={topikStatus.error}
      onBack={() => dispatch(topikActions.analysis.back.request())}
    >
      <AnalysisRecall
        entries={[
          { key: "risk", label: t("topik-analysis.recall.risk"), value: session.risk },
          { key: "solution", label: t("topik-analysis.recall.solution"), value: session.solution },
        ]}
      />
      <div className="topik-analysis__gate">
        <button
          type="button"
          className="topik-analysis__gate-option topik-analysis__gate-option--continue"
          onClick={() => choose(true)}
          disabled={topikStatus.submitting || gameClosed}
        >
          <span className="topik-analysis__gate-icon" aria-hidden="true">
            <AddCircleOutlineIcon />
          </span>
          <span className="topik-analysis__gate-text">
            <strong>{t("topik-continue-view.continue-label")}</strong>
            <span>{t("topik-continue-view.continue-hint")}</span>
          </span>
          <ArrowForwardIcon className="topik-analysis__gate-arrow" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="topik-analysis__gate-option topik-analysis__gate-option--stop"
          onClick={() => choose(false)}
          disabled={topikStatus.submitting || gameClosed}
        >
          <span className="topik-analysis__gate-icon" aria-hidden="true">
            <CheckCircleOutlineIcon />
          </span>
          <span className="topik-analysis__gate-text">
            <strong>{t("topik-continue-view.stop-label")}</strong>
            <span>{t("topik-continue-view.stop-hint")}</span>
          </span>
          <ArrowForwardIcon className="topik-analysis__gate-arrow" aria-hidden="true" />
        </button>
      </div>
    </AnalysisFrame>
  );
};

TopikContinueView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  topikStatus: PropTypes.object.isRequired,
  gameClosed: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

TopikContinueView.defaultProps = {
  gameClosed: false,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikContinueView);
